import { useEffect, useRef, useState } from "react";
import {
  registerConfirmSink,
  type ConfirmRequest,
} from "../../../integrations/tools/outlook/confirm-bridge";
import styles from "./ChatConfirmDialog.module.css";

interface PendingConfirm {
  readonly request: ConfirmRequest;
  readonly resolve: (confirmed: boolean) => void;
}

export function ChatConfirmDialog(): React.ReactElement | null {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const unsubscribe = registerConfirmSink(
      (request) =>
        new Promise<boolean>((resolve) => {
          setPending({ request, resolve });
        }),
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (pending) confirmButtonRef.current?.focus();
  }, [pending]);

  if (!pending) return null;

  const answer = (confirmed: boolean): void => {
    pending.resolve(confirmed);
    setPending(null);
  };

  const { request } = pending;

  return (
    <div className={styles.overlay}>
      <dialog open className={styles.dialog} aria-modal="true" aria-label={request.title}>
        <h2 className={styles.title}>{request.title}</h2>
        <p className={styles.message}>{request.message}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={() => answer(false)}>
            {request.cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmButtonRef}
            className={styles.confirm}
            onClick={() => answer(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </dialog>
    </div>
  );
}
